import { isNetworkError } from './networkErrorUtils';

export const MAX_DISCOVERY_SESSION_ATTEMPTS = 3;
const DISCOVERY_SESSION_RETRY_BASE_DELAY_MS = 500;
// i18n-ignore: technical-token -- internal retry sentinel, never shown to customers.
const SESSION_PROPAGATION_ERROR_MESSAGE = 'Session not established after signInAnonymously';

type RetryOptions = {
    maxAttempts?: number;
    wait?: (ms: number) => Promise<void>;
    onRetry?: (details: {
        attempt: number;
        maxAttempts: number;
        delayMs: number;
        error: Error;
    }) => void;
};

export function shouldRetryDiscoverySessionBootstrap(error: unknown): boolean {
    const normalizedError = error instanceof Error ? error : new Error(String(error));

    if (isNetworkError(normalizedError)) {
        return true;
    }

    return normalizedError.message.includes(SESSION_PROPAGATION_ERROR_MESSAGE);
}

export function getDiscoverySessionRetryDelayMs(attempt: number): number {
    return DISCOVERY_SESSION_RETRY_BASE_DELAY_MS * Math.pow(2, Math.max(0, attempt - 1));
}

export async function retryDiscoverySessionBootstrap<T>(
    operation: (attempt: number) => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const maxAttempts = options.maxAttempts ?? MAX_DISCOVERY_SESSION_ATTEMPTS;
    const wait = options.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            return await operation(attempt);
        } catch (error) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));

            if (!shouldRetryDiscoverySessionBootstrap(normalizedError) || attempt === maxAttempts) {
                throw normalizedError;
            }

            const delayMs = getDiscoverySessionRetryDelayMs(attempt);
            options.onRetry?.({
                attempt,
                maxAttempts,
                delayMs,
                error: normalizedError,
            });
            await wait(delayMs);
        }
    }

    throw new Error('Discovery session retry exhausted');
}
